import { useCallback } from 'react';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebSocket } from 'api/hooks';

import {
  ChatSessionResponse,
  PaginatedMessagesResponse,
  PaginationParams,
  ProviderStatusResponse,
  acceptPlan,
  answerQuestions,
  decideEnterPlanMode,
  deleteAIBuilderHistory,
  getProviderStatus,
  interruptAIBuilder,
  loadAIBuilderChatSession,
  loadAIBuilderExamplePrompts,
  loadAIBuilderMessages,
  loadAIBuilderNewMessages,
  pickResource,
  runAIBuilder,
  transcribeAudio,
  updateSessionMode,
} from './requests';

export const getAiBuilderMessagesQueryKey = (journeyShortId?: string) => [
  'ai-builder-messages',
  journeyShortId,
];

export const getAiBuilderSessionQueryKey = (journeyShortId?: string) => [
  'ai-builder-chat-session',
  journeyShortId,
];

const useInvalidateAiBuilderMessages = () => {
  const queryClient = useQueryClient();

  const invalidateMessages = (journeyShortId: string) => {
    queryClient.invalidateQueries(getAiBuilderMessagesQueryKey(journeyShortId));
  };

  return {
    invalidateMessages,
  };
};

const PROVIDER_STATUS_QUERY_KEY = ['ai-builder-provider-status'];

export const useProviderStatus = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: PROVIDER_STATUS_QUERY_KEY,
    queryFn: getProviderStatus,
    enabled: options?.enabled,
  });
};

export const useProviderStatusWebSocket = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (event: MessageEvent<string>) => {
      if (!event?.data) return;
      const envelope = JSON.parse(event.data);
      if (envelope?.type !== 'update' || !envelope.payload?.status) return;

      queryClient.setQueryData(
        PROVIDER_STATUS_QUERY_KEY,
        (old: ProviderStatusResponse | undefined) => ({
          ...old,
          status: envelope.payload.status,
        })
      );
    },
    [queryClient]
  );

  useWebSocket('ai-builder/provider-status/', handleMessage, {
    enabled: options?.enabled ?? true,
    throttleTime: 0,
  });
};

export const useRunAIBuilder = () => {
  return useMutation({
    mutationFn: runAIBuilder,
  });
};

export const useInterruptAIBuilder = () => {
  return useMutation({
    mutationFn: interruptAIBuilder,
  });
};

export const useAIBuilderMessagesInfinite = (
  journeyShortId: string,
  options?: { enabled?: boolean }
) => {
  return useInfiniteQuery({
    queryKey: getAiBuilderMessagesQueryKey(journeyShortId),
    queryFn: ({ pageParam }) =>
      loadAIBuilderMessages(journeyShortId, pageParam as PaginationParams | undefined),
    getNextPageParam: (lastPage: PaginatedMessagesResponse) =>
      lastPage.hasMore ? { beforeId: lastPage.oldestMessageId ?? undefined } : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: options?.enabled,
  });
};

export const useAIBuilderChatSession = (journeyShortId: string) => {
  return useQuery({
    queryKey: getAiBuilderSessionQueryKey(journeyShortId),
    queryFn: () => loadAIBuilderChatSession(journeyShortId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useAIBuilderExamplePrompts = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['ai-builder-example-prompts'],
    queryFn: loadAIBuilderExamplePrompts,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: options?.enabled,
  });
};

export const useAIBuilderNewMessages = () => {
  return useMutation({
    mutationFn: loadAIBuilderNewMessages,
  });
};

type DeleteAIBuilderHistoryParams = {
  journeyShortId: string;
};

export const useDeleteAIBuilderHistory = () => {
  return useMutation({
    mutationFn: ({ journeyShortId }: DeleteAIBuilderHistoryParams) =>
      deleteAIBuilderHistory(journeyShortId),
  });
};

export const useTranscribeAudio = () => {
  return useMutation({
    mutationFn: transcribeAudio,
  });
};

export const useAnswerQuestions = () => {
  return useMutation({
    mutationFn: answerQuestions,
  });
};

export const usePickResource = () => {
  return useMutation({
    mutationFn: pickResource,
  });
};

export const useUpdateSessionMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSessionMode,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        getAiBuilderSessionQueryKey(variables.journeyShortId),
        (old: ChatSessionResponse | undefined) => {
          if (!old) {
            return {
              shortId: data.shortId,
              mode: data.mode,
              isGenerating: false,
            };
          }
          return {
            ...old,
            shortId: data.shortId,
            mode: data.mode,
          };
        }
      );
    },
  });
};

export const useAcceptPlan = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: acceptPlan,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};

export const useEnterPlanModeDecision = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: decideEnterPlanMode,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};
